// M004-F0759 / F0760 (WebUI #205 / #206): pure rules for customer statements, coded to Spec core-ledger-account-gateway 1.18.0
// (ML001-F0290). No I/O and no imports, so the rules are unit-testable on their own.
//
// Kinds: `outstanding` (the default: unpaid invoices as at a date) or `activity` (bookings from-to).
// A PREVIEW (statement.pdf) is watermarked PREVIEW and carries NO statement number; it never consumes one.
// Only an ISSUED statement carries `statement_number` (assigned by the Ledger, never by this client).

export const STATEMENT_KINDS = Object.freeze(['outstanding', 'activity']);
export const STATEMENT_DEFAULT_KIND = 'outstanding';
// Issuing needs ledger_manage (Spec statement.issue, ledger_manage). Preview and list also accept billing_transactions_view.
export const STATEMENT_MANAGE_KEY = 'ledger_manage';
export const STATEMENT_KIND_LABELS = Object.freeze({ outstanding: 'Outstanding as at', activity: 'Activity from–to' });
export const STATEMENT_ISSUER_DETAILS_REQUIRED = 'LEDGER_STATEMENT_ISSUER_DETAILS_REQUIRED';
// M004-F0801 (WebUI #266 slice 1): the Ledger serves NONE of statement.list / statement.pdf / statement.issue / statement.document.get on
// prod (3a93237c), so the statement-document surfaces (StatementsList, StatementGenerateDialog, the Generate button) are not rendered.
// Flip to true only when the Ledger serves them. A code constant, reviewed like any other change; the components and their tests stay.
export const STATEMENT_DOCUMENTS_SERVED = false;
// The three settings the Ledger can name as missing (Spec NodeRefusalLedgerStatementDetailsRequired.missing, openapi.json).
export const STATEMENT_MISSING_LABELS = Object.freeze({
  bank_details: 'Bank details',
  registered_office: 'Registered office',
  payment_terms: 'Payment terms',
});

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
// A real calendar date: the round trip through Date must return the same string (rejects 2026-02-30, which Date would roll over).
const isoDate = (value) => typeof value === 'string' && ISO_DATE.test(value)
  && new Date(`${value}T00:00:00Z`).toISOString().slice(0, 10) === value;

export const kindLabel = (kind) => STATEMENT_KIND_LABELS[kind] || String(kind || '');

// dd/mm/yyyy for an ISO date (the Ledger's dates are calendar dates, never instants).
export const formatDate = (iso) => {
  if (!isoDate(iso)) return String(iso || '');
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y}`;
};

// Request body for statement.pdf / statement.issue (Spec StatementRenderBody). Fails closed: a missing or malformed date is refused
// here, before any request, so no default date is ever sent.
// The Core user id of the customer: a positive whole number (a numeric string is accepted and converted). Refused otherwise.
// Spec EntityId for customer_id is a STRING (^[A-Za-z0-9._:-]+$); the relay target_user_id is the number (Core compares it).
export function customerIdOf(value) {
  const id = typeof value === 'string' && /^\d+$/.test(value) ? Number(value) : value;
  if (!Number.isSafeInteger(id) || id < 1) throw new Error('A customer is required for a statement');
  return id;
}

export function buildStatementBody({ customerId, kind = STATEMENT_DEFAULT_KIND, asAt, from, to }) {
  const id = customerIdOf(customerId);
  if (kind === 'outstanding') {
    if (!isoDate(asAt)) throw new Error('An outstanding statement needs an as-at date');
    return { customer_id: String(id), kind, as_at: asAt };
  }
  if (kind === 'activity') {
    if (!isoDate(from) || !isoDate(to)) throw new Error('An activity statement needs a from and a to date');
    if (from > to) throw new Error('The from date is after the to date');
    return { customer_id: String(id), kind, from, to };
  }
  throw new Error(`Unknown statement kind: ${kind}`);
}

// Signed balance: positive = due, negative = in credit (Spec statement balance_minor, as in statement.get).
export function balanceLabel({ balance_minor: minor, currency = 'GBP' } = {}) {
  const value = Number(minor) || 0;
  const amount = new Intl.NumberFormat('en-GB', { style: 'currency', currency }).format(Math.abs(value) / 100);
  if (value < 0) return `${amount} In credit`;
  return amount;
}

export const asAtOrPeriod = (row) => (row?.kind === 'activity'
  ? `${formatDate(row.period_from)} to ${formatDate(row.period_to)}`
  : formatDate(row?.as_at));

// Newest issued first (by issued_at, then number). Never mutates the input.
export function sortNewestFirst(rows) {
  return [...(Array.isArray(rows) ? rows : [])].sort((a, b) => {
    if (a.issued_at !== b.issued_at) return a.issued_at < b.issued_at ? 1 : -1;
    return a.statement_number < b.statement_number ? 1 : a.statement_number > b.statement_number ? -1 : 0;
  });
}

// The preview rule: a preview is watermarked PREVIEW and is never numbered. Throws when a preview carries a number or lacks the mark,
// so a mis-wired preview cannot be shown as if it were issued.
export function assertUnnumberedPreview(payload) {
  if (!payload || typeof payload !== 'object') throw new Error('The Ledger sent no preview');
  if (payload.watermark !== 'PREVIEW') throw new Error('A statement preview must carry the PREVIEW watermark');
  if (payload.statement_number !== undefined && payload.statement_number !== null) {
    throw new Error('A statement preview must not carry a statement number');
  }
  return payload;
}

// The number shown after an issue: the Ledger's value, verbatim. Never a default.
export const issuedNumber = (issued) => issued?.statement?.statement_number || null;

// The missing settings a refusal names, mapped to labels. Unknown codes are kept verbatim (never dropped or invented).
export const missingSettingLabels = (missing) => (Array.isArray(missing) ? missing : [])
  .map((code) => STATEMENT_MISSING_LABELS[code] || String(code));
